import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { COLORS } from "../../assets/theme";
import { getClassSessions } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassDefinition, ClassSession } from "./types";
import {
  SESSION_STATUS_COLORS,
  SessionRange,
  currentMonthRange,
  formatRangeLabel,
  formatStatusLabel,
  formatTime,
  isOpenEndedClass,
  isSessionCancelled,
  isSessionPast,
  sessionRowKey,
  shiftRangeByMonths,
} from "./classUtils";
import SessionRosterDialog from "./SessionRosterDialog";
import CancelClassSessionDialog from "./CancelClassSessionDialog";

interface ClassSessionsDialogProps {
  open: boolean;
  businessId?: string;
  classItem: ClassDefinition | null;
  onClose: () => void;
}

/**
 * A window into the class's sessions, not the whole series — an open-ended
 * class has no "every session" to list, so the month is the unit of browsing
 * and the arrows are unbounded on purpose.
 *
 * Rows come back in two states: real appointment rows, and dates the recurrence
 * rule projects but that nothing has been written for yet. Both are actionable
 * — the roster and cancel endpoints take a projected id and materialize it —
 * but a projected row's id changes the moment that happens, which is why every
 * action refetches the window rather than patching a row in place.
 */
const ClassSessionsDialog: React.FC<ClassSessionsDialogProps> = ({
  open,
  businessId,
  classItem,
  onClose,
}) => {
  const [sessions, setSessions] = React.useState<ClassSession[]>([]);
  const [range, setRange] = React.useState<SessionRange>(() => currentMonthRange());
  const [loading, setLoading] = React.useState(false);
  const [rosterSessionId, setRosterSessionId] = React.useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = React.useState<ClassSession | null>(null);

  const classId = classItem?.id ? String(classItem.id) : "";

  React.useEffect(() => {
    if (open) setRange(currentMonthRange());
  }, [open, classId]);

  const fetchSessions = React.useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const result = await getClassSessions(classId, range);
      setSessions(result.sessions);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load the class sessions."));
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [classId, range]);

  React.useEffect(() => {
    if (!open || !classId) return;
    fetchSessions();
  }, [open, classId, fetchSessions]);

  const capacity = classItem?.capacity;
  const openEnded = isOpenEndedClass(classItem || undefined);

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        paperSx={{ minWidth: { sm: 760 }, maxWidth: 960 }}
        title={
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              {`Sessions — ${classItem?.name || "Class"}`}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#666" }}>
              {`${classItem?.coach?.full_name || "No coach"} · ${
                classItem?.unit?.name || "-"
              }${capacity ? ` · capacity ${capacity}` : ""}${
                openEnded ? " · runs until further notice" : ""
              }`}
            </Typography>
          </Box>
        }
        actions={<CustomButton label="Close" variant="outlined" onClick={onClose} />}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => setRange((prev) => shiftRangeByMonths(prev, -1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
            {formatRangeLabel(range)}
          </Typography>
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => setRange((prev) => shiftRangeByMonths(prev, 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : sessions.length === 0 ? (
          <Typography sx={{ color: "#777", fontSize: 14 }}>
            No sessions in this month.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Booked</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session, index) => {
                  const cancelled = isSessionCancelled(session.status);
                  const statusKey = String(session.status || "").toUpperCase();
                  const bookedCount = session.booked_count;
                  const projected = session.is_virtual === true;
                  const isFull =
                    typeof bookedCount === "number" &&
                    typeof capacity === "number" &&
                    capacity > 0 &&
                    bookedCount >= capacity;

                  return (
                    <TableRow
                      key={sessionRowKey(session, index)}
                      sx={{ opacity: cancelled || isSessionPast(session.end_at) ? 0.6 : 1 }}
                    >
                      <TableCell>
                        {formatTableDate(session.start_at)}
                        {projected && (
                          <Tooltip title="Generated from the class schedule — it becomes a real session the first time someone books it.">
                            <Chip
                              size="small"
                              label="Projected"
                              variant="outlined"
                              sx={{
                                ml: 1,
                                height: 20,
                                fontSize: 11,
                                borderStyle: "dashed",
                                color: "#6b46c1",
                                borderColor: "#b794f4",
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {`${formatTime(session.start_at)} - ${formatTime(
                          session.end_at
                        )}`}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: isFull ? 700 : 500,
                          color: isFull ? "#d32f2f" : "inherit",
                        }}
                      >
                        {typeof bookedCount === "number"
                          ? `${bookedCount}${capacity ? ` / ${capacity}` : ""}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatStatusLabel(session.status)}
                          sx={{
                            bgcolor: `${
                              SESSION_STATUS_COLORS[statusKey] || "#666"
                            }1a`,
                            color: SESSION_STATUS_COLORS[statusKey] || "#666",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Membership Clients">
                          <IconButton
                            size="small"
                            onClick={() => setRosterSessionId(String(session.id))}
                          >
                            <GroupIcon sx={{ color: COLORS.MainColor, width: 20 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel session">
                          <span>
                            <IconButton
                              size="small"
                              disabled={cancelled}
                              onClick={() => setCancelTarget(session)}
                            >
                              <EventBusyIcon
                                sx={{
                                  color: cancelled ? "#D3D3D3" : "#d32f2f",
                                  width: 20,
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CustomDialog>

      <SessionRosterDialog
        open={Boolean(rosterSessionId)}
        businessId={businessId}
        sessionId={rosterSessionId}
        onClose={() => {
          setRosterSessionId(null);
          // Booking into a projected session turns it into a real row with a
          // different id, so the list is refetched on close either way.
          void fetchSessions();
        }}
        onRosterChanged={fetchSessions}
      />

      <CancelClassSessionDialog
        open={Boolean(cancelTarget)}
        session={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={fetchSessions}
      />
    </>
  );
};

export default ClassSessionsDialog;
